import { Container, type Ticker } from 'pixi.js';
import { Easings, TweenGroup } from '../../core/Tween';
import { WinParticles } from '../../effects/WinParticles';
import { CoinAmountLabel } from '../../widgets/CoinAmountLabel';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

/**
 * Bingo win celebration: particle burst + "+N <coin>" popup. Centred on the
 * viewport (over the card), sized by the win amount.
 *
 * <p>Distinct from the slot / wheel win sequences because the bingo pop-up
 * doesn't drift up — it just fades out in place after a beat, matching the
 * scene's calmer pacing.
 */
export interface BingoWinSequenceOptions {
  /** Popup fill colour. Defaults to warm gold. */
  fillColor?: number;
  /** Popup stroke colour. Defaults to brand pink. */
  strokeColor?: number;
}

export class BingoWinSequenceComponent extends Container implements SceneComponent {
  private readonly popup: CoinAmountLabel;
  private readonly particles: WinParticles;
  private readonly tweens = new TweenGroup();

  constructor(ticker: Ticker, options: BingoWinSequenceOptions = {}) {
    super();
    this.particles = new WinParticles(ticker);
    this.addChild(this.particles);

    this.popup = new CoinAmountLabel('', {
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 30,
        fontWeight: '900',
        fill: options.fillColor ?? 0xfff59d,
        align: 'center',
        stroke: { color: options.strokeColor ?? 0xff6bd6, width: 4 },
      },
    });
    this.popup.alpha = 0;
    this.addChild(this.popup);
  }

  layout(ctx: SceneLayoutContext): void {
    const cx = ctx.centerX;
    const cy = Math.round(ctx.height / 2);
    this.particles.x = cx;
    this.particles.y = cy;
    this.popup.x = cx;
    this.popup.y = cy;
  }

  destroy(): void {
    this.tweens.cancelAll();
    this.particles.destroyEffect();
    super.destroy({ children: true });
  }

  playWin(winnings: number): void {
    this.particles.burst(Math.min(180, 60 + Math.floor(winnings * 0.4)), {
      tints: [0xff6bd6, 0xfff59d, 0x42e2f5, 0xffd23b],
      lifeMs: 1300,
      minSpeed: 240,
      maxSpeed: 540,
      gravity: 700,
      radius: 5,
    });

    this.popup.setText(`+${winnings.toLocaleString()}`);
    this.popup.alpha = 0;
    this.popup.scale.set(0.6);

    this.tweens.start({
      durationMs: 240,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        this.popup.alpha = p;
        this.popup.scale.set(0.6 + p * 0.6);
      },
      onComplete: () => {
        this.tweens.start({
          durationMs: 1000,
          easing: Easings.easeOutCubic,
          onUpdate: (p) => {
            this.popup.alpha = 1 - p;
          },
        });
      },
    });
  }
}
